"use client";
// beui.dev/components/motion/animated-badge
// Restyled for Bavimail (DESIGN.md small mono labels): the label is Chivo Mono,
// UPPERCASE and tracked, the surface is a sharp `rounded-sm` tag by default (the
// dashboard's status chips) and status colours come from the hb-* palette; `info`
// is the one muted blue the brand allows. Upstream's roll/rotate/blur icon swap
// and the scale "pulse" ring are replaced by a quiet slide + fade on the shared
// SPRING_SWAP and an opacity pulse, per the no-bounce motion rule.
// Bavimail additions: status `accent` (orange, live/primary tags only), `shape`
// ("tag" | "pill"), `variant` ("soft" | "outline" | "solid"), `dot` (6px status
// dot in the leading slot), `caps` (set false to keep identifiers verbatim).
// Default change vs upstream: `showIcon` defaults to false unless an `icon` is
// passed or `status === "loading"` — Bavimail badges are label-only.

import { AlertTriangle, Check, Circle, Info, X, type LucideIcon } from "lucide-react";
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  type HTMLMotionProps,
  type Variants,
} from "motion/react";
import type { ReactNode } from "react";
import { EASE_OUT, SPRING_LAYOUT, SPRING_SWAP } from "@/lib/ease";
import { cn } from "@/lib/utils";

export type AnimatedBadgeStatus =
  | "neutral"
  | "info"
  | "success"
  | "warning"
  | "danger"
  | "loading"
  | "accent";

export type AnimatedBadgeSize = "sm" | "md";
export type AnimatedBadgeShape = "tag" | "pill";
export type AnimatedBadgeVariant = "soft" | "outline" | "solid";

export interface AnimatedBadgeProps extends Omit<HTMLMotionProps<"span">, "children"> {
  status?: AnimatedBadgeStatus;
  size?: AnimatedBadgeSize;
  children?: ReactNode;
  icon?: ReactNode;
  /** Default: true when `icon` is given or `status === "loading"`, else false. */
  showIcon?: boolean;
  /** Default: `status === "loading"`. Pulses the dot (or the tint when there is none). */
  pulse?: boolean;
  contentKey?: string | number;
  /** Sharp `rounded-sm` tag (default) or `rounded-full` pill. */
  shape?: AnimatedBadgeShape;
  /** `soft` = tint + hairline (default), `outline` = dashed hairline only, `solid` = filled. */
  variant?: AnimatedBadgeVariant;
  /** 6px status dot in the leading slot (ignored when an icon is shown). */
  dot?: boolean;
  /** Uppercase + tracked label (default). Set false for identifiers such as event names. */
  caps?: boolean;
}

const SOFT_CLASS: Record<AnimatedBadgeStatus, string> = {
  neutral: "border-hb-card bg-hb-raised/40 text-hb-muted",
  info: "border-hb-info/25 bg-hb-info/10 text-hb-info",
  success: "border-hb-success/25 bg-hb-success/10 text-hb-success",
  warning: "border-hb-warning/25 bg-hb-warning/10 text-hb-warning",
  danger: "border-hb-danger/25 bg-hb-danger/10 text-hb-danger",
  loading: "border-hb-accent/25 bg-hb-accent/10 text-hb-accent",
  accent: "border-hb-accent/25 bg-hb-accent/10 text-hb-accent",
};

const OUTLINE_CLASS: Record<AnimatedBadgeStatus, string> = {
  neutral: "border-dashed border-hb-card bg-transparent text-hb-muted",
  info: "border-dashed border-hb-info/40 bg-transparent text-hb-info",
  success: "border-dashed border-hb-success/40 bg-transparent text-hb-success",
  warning: "border-dashed border-hb-warning/40 bg-transparent text-hb-warning",
  danger: "border-dashed border-hb-danger/40 bg-transparent text-hb-danger",
  loading: "border-dashed border-hb-accent/40 bg-transparent text-hb-accent",
  accent: "border-dashed border-hb-accent/40 bg-transparent text-hb-accent",
};

const SOLID_CLASS: Record<AnimatedBadgeStatus, string> = {
  neutral: "border-hb-card bg-hb-raised text-hb-heading",
  info: "border-transparent bg-hb-info text-hb-accent-fg",
  success: "border-transparent bg-hb-success text-hb-accent-fg",
  warning: "border-transparent bg-hb-warning text-hb-accent-fg",
  danger: "border-transparent bg-hb-danger text-hb-accent-fg",
  loading: "border-transparent bg-hb-accent text-hb-accent-fg",
  accent: "border-transparent bg-hb-accent text-hb-accent-fg",
};

const VARIANT_CLASS: Record<AnimatedBadgeVariant, Record<AnimatedBadgeStatus, string>> = {
  soft: SOFT_CLASS,
  outline: OUTLINE_CLASS,
  solid: SOLID_CLASS,
};

const SIZE_CLASS: Record<AnimatedBadgeSize, string> = {
  sm: "h-5 gap-1 px-1.5 text-[9px]",
  md: "h-6 gap-1.5 px-2 text-[10px]",
};

const CAPS_CLASS: Record<AnimatedBadgeSize, string> = {
  sm: "uppercase tracking-[0.1em]",
  md: "uppercase tracking-[0.12em]",
};

const SHAPE_CLASS: Record<AnimatedBadgeShape, string> = {
  tag: "rounded-sm",
  pill: "rounded-full",
};

const ICON_CLASS: Record<AnimatedBadgeSize, string> = {
  sm: "h-2.5 w-2.5",
  md: "h-3 w-3",
};

const ICONS: Record<Exclude<AnimatedBadgeStatus, "loading">, LucideIcon> = {
  neutral: Circle,
  info: Info,
  success: Check,
  warning: AlertTriangle,
  danger: X,
  accent: Circle,
};

// Quiet swap: a short slide + fade on the shared swap spring. No rotate, no
// overshoot; a faint blur softens the crossfade without reading as motion.
const ICON_SWAP_VARIANTS: Variants = {
  initial: { opacity: 0, y: "40%", filter: "blur(3px)" },
  animate: {
    opacity: 1,
    y: "0%",
    filter: "blur(0px)",
    transition: {
      y: SPRING_SWAP,
      opacity: { duration: 0.18, ease: EASE_OUT },
      filter: { duration: 0.24, ease: EASE_OUT },
    },
  },
  exit: {
    opacity: 0,
    y: "-40%",
    filter: "blur(3px)",
    transition: { duration: 0.16, ease: EASE_OUT },
  },
};

const TEXT_SWAP_VARIANTS: Variants = {
  initial: { opacity: 0, y: "45%", filter: "blur(3px)" },
  animate: {
    opacity: 1,
    y: "0%",
    filter: "blur(0px)",
    transition: {
      y: SPRING_SWAP,
      opacity: { duration: 0.2, ease: EASE_OUT },
      filter: { duration: 0.24, ease: EASE_OUT },
    },
  },
  exit: {
    opacity: 0,
    y: "-45%",
    filter: "blur(3px)",
    transition: { duration: 0.16, ease: EASE_OUT },
  },
};

const PULSE = {
  animate: { opacity: [1, 0.35, 1] },
  transition: { duration: 1.6, repeat: Infinity, ease: "easeInOut" as const },
};

// Same geometry as `loader.tsx`'s spinner (faint track + quarter arc), sized to
// the badge's icon slot so a loading badge matches the rest of the loaders.
function BadgeSpinner({ size, reduce }: { size: AnimatedBadgeSize; reduce: boolean }) {
  const px = size === "sm" ? 10 : 12;
  const stroke = 2;
  const r = (px - stroke) / 2;
  const c = px / 2;
  return (
    <motion.svg
      width={px}
      height={px}
      viewBox={`0 0 ${px} ${px}`}
      className="shrink-0"
      animate={reduce ? PULSE.animate : { rotate: 360 }}
      transition={reduce ? PULSE.transition : { duration: 1, ease: "linear", repeat: Infinity }}
    >
      <circle
        cx={c}
        cy={c}
        r={r}
        fill="none"
        stroke="currentColor"
        strokeOpacity={0.2}
        strokeWidth={stroke}
      />
      <path
        d={`M ${c} ${c - r} A ${r} ${r} 0 0 1 ${c + r} ${c}`}
        fill="none"
        stroke="currentColor"
        strokeWidth={stroke}
        strokeLinecap="round"
      />
    </motion.svg>
  );
}

export function AnimatedBadge({
  status = "neutral",
  size = "md",
  children,
  icon,
  showIcon = icon != null || status === "loading",
  pulse = status === "loading",
  contentKey,
  shape = "tag",
  variant = "soft",
  dot = false,
  caps = true,
  className,
  ...rest
}: AnimatedBadgeProps) {
  const reduce = useReducedMotion() ?? false;
  const Icon = status === "loading" ? null : ICONS[status];
  const resolvedContentKey =
    contentKey ??
    (typeof children === "string" || typeof children === "number" ? children : status);
  const showDot = dot && !showIcon;
  // The dot carries the pulse when there is one; otherwise the tint breathes.
  const tintPulse = pulse && !reduce && !showDot;

  return (
    <motion.span
      layout
      transition={SPRING_LAYOUT}
      className={cn(
        "font-mono-hb relative inline-flex shrink-0 items-center overflow-hidden whitespace-nowrap border leading-none tabular-nums",
        "transition-colors duration-[var(--motion-duration-fast)]",
        VARIANT_CLASS[variant][status],
        SIZE_CLASS[size],
        caps && CAPS_CLASS[size],
        SHAPE_CLASS[shape],
        className,
      )}
      {...rest}
    >
      {tintPulse ? (
        <motion.span
          aria-hidden
          className={cn("absolute inset-0 bg-current", SHAPE_CLASS[shape])}
          animate={{ opacity: [0.04, 0.12, 0.04] }}
          transition={PULSE.transition}
        />
      ) : null}
      {showDot ? (
        <motion.span
          aria-hidden
          data-badge-dot
          className="relative z-10 h-1.5 w-1.5 shrink-0 rounded-full bg-current"
          animate={pulse && !reduce ? PULSE.animate : { opacity: 1 }}
          transition={pulse && !reduce ? PULSE.transition : undefined}
        />
      ) : null}
      {showIcon ? (
        <span className="relative z-10 inline-flex items-center justify-center overflow-hidden">
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.span
              key={status}
              aria-hidden
              data-badge-icon
              variants={ICON_SWAP_VARIANTS}
              initial={reduce ? false : "initial"}
              animate={reduce ? { opacity: 1 } : "animate"}
              exit={reduce ? undefined : "exit"}
              className="inline-flex will-change-transform"
            >
              {icon ??
                (Icon ? (
                  <Icon className={ICON_CLASS[size]} />
                ) : (
                  <BadgeSpinner size={size} reduce={reduce} />
                ))}
            </motion.span>
          </AnimatePresence>
        </span>
      ) : null}
      {children != null ? (
        <span className="relative z-10 inline-flex overflow-hidden">
          <AnimatePresence mode="popLayout" initial={false}>
            <motion.span
              key={resolvedContentKey}
              data-badge-label
              variants={TEXT_SWAP_VARIANTS}
              initial={reduce ? false : "initial"}
              animate={reduce ? { opacity: 1 } : "animate"}
              exit={reduce ? undefined : "exit"}
              className="inline-block will-change-transform"
            >
              {children}
            </motion.span>
          </AnimatePresence>
        </span>
      ) : null}
    </motion.span>
  );
}
